import { useCallback, useEffect, useMemo } from 'react';
import { useFeatureFlagEnabled } from '@posthog/react';
import { useNavigate } from '@tanstack/react-router';

import { useGetProductCategoriesQuery } from '@/api/hooks/queries/use-get-product-categories-query';
import { Button } from '@/components/ui/button';
import { useReferrerProduct } from '@/hooks/use-referrer-product';
import { useSessionSave } from '@/hooks/use-session-save';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { trackInputPatientSelectedCategory } from '@/lib/analytics';
import { hasProductStep, isCategorySelectionCard } from '@/lib/products';
import { FALLBACK_WIZARD_VARIANT_ID } from '@/lib/wizard/wizard.config';

import { ProductCategories } from './ProductCategories';

export function ProductCategorySelection() {
  const {
    data: categories,
    isLoading: isLoadingCategories,
    isError,
    isRefetching,
    refetch,
  } = useGetProductCategoriesQuery();

  const isEligibilityRevampEnabled = useFeatureFlagEnabled('eligibility-insurance-revamp');
  const selectedCategoryIds = useSessionStore((state) => state.selectedCategoryIds);
  const {
    referrerProductId,
    product: referrerProduct,
    isLoading: isLoadingReferrerProduct,
    isNotFound: isReferrerProductNotFound,
  } = useReferrerProduct();

  const navigate = useNavigate();
  const { saveSession } = useSessionSave();
  const storeAndContinue = useCallback(
    (categoryIds: string[]) => {
      const { updateState, contactEmail } = useSessionStore.getState();
      const categoryLabels =
        categories?.filter((cat) => categoryIds.includes(cat.uuid)).map((cat) => cat.label) ?? [];

      updateState({
        selectedCategoryIds: categoryIds,
        wizardVariantId: isEligibilityRevampEnabled
          ? 'eligibility-revamp'
          : FALLBACK_WIZARD_VARIANT_ID,
      });

      trackInputPatientSelectedCategory({
        email: contactEmail,
        supplyCategories: categoryLabels,
      });

      // Auto-save session (non-blocking)
      saveSession();

      navigate({ to: '/1' });
    },
    [navigate, categories, saveSession, isEligibilityRevampEnabled]
  );

  const hasUnusableReferrer =
    isReferrerProductNotFound ||
    (referrerProduct != null && !hasProductStep(referrerProduct.category.name));

  useEffect(() => {
    if (hasUnusableReferrer) {
      useSessionStore.getState().updateState({ referrerProductId: null });
    }
  }, [hasUnusableReferrer]);

  const referrerCategoryId = hasUnusableReferrer
    ? undefined
    : referrerProduct?.category.uuid || undefined;
  const categoryOptions = useMemo(
    () => (categories ?? []).filter((category) => isCategorySelectionCard(category.label)),
    [categories]
  );
  const isLoading = isLoadingCategories || Boolean(referrerProductId && isLoadingReferrerProduct);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" role="status">
        <p className="text-charcoal-500 italic">Loading your items...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12" role="alert">
        <p className="text-error">There was a problem loading your items. Please try again.</p>
        <Button onClick={() => refetch()} disabled={isRefetching} variant="outline">
          {isRefetching ? 'Retrying...' : 'Retry'}
        </Button>
      </div>
    );
  }

  return (
    <ProductCategories
      categories={categoryOptions}
      referrerCategoryId={referrerCategoryId}
      selectedCategoryIds={selectedCategoryIds}
      onSubmit={storeAndContinue}
    />
  );
}
